/**
 * Zemax container formats (.zar archives and .zprj lens projects).
 *
 * Both are the same block-sequence structure with different header layouts:
 * repeated [version tag | fixed-size header | payload] blocks. Members whose
 * stored name ends in ".LZW" are compressed with a variable-code-width LZW.
 * Layouts reverse-engineered and validated 2026-08-25 against real archives
 * from OpticStudio 2010→26.1 (see docs/superpowers/plans/
 * 2026-08-25-zemax-container-import.md, Format Reference).
 *
 * Modern .zos and SaveAs-written .zprj files are plain ZMX text — callers
 * sniff bytes with sniffZemaxBytes() and only route real containers here.
 */

interface ContainerLayout {
  headerLength: number;
  sizeOffset: number;
  sizeBytes: 4 | 8;
  nameOffset: number;
  nameEncoding: 'utf16le' | 'latin1';
}

const ZAR_LATEST: ContainerLayout = { headerLength: 0x288, sizeOffset: 0x10, sizeBytes: 8, nameOffset: 0x30, nameEncoding: 'utf16le' };
// Pre-~2012 ZARs (first header byte 0xEA). No sample obtainable in 2026; layout
// from public reverse engineering. The walker enforces exact-EOF alignment, so
// a wrong layout fails loudly rather than importing garbage.
const ZAR_EARLY: ContainerLayout = { headerLength: 0x14c, sizeOffset: 0x0c, sizeBytes: 4, nameOffset: 0x20, nameEncoding: 'latin1' };
const ZPRJ: ContainerLayout = { headerLength: 0x4e8, sizeOffset: 0x18, sizeBytes: 8, nameOffset: 0x38, nameEncoding: 'utf16le' };

export type ZemaxByteKind = 'zmx-text' | 'zar' | 'zprj' | 'unknown';

// ZMXParser._detectEncoding (zmxToJson.ts) accepts all of these; the sniff
// here must recognize the same set so a text file never mis-falls-through to
// the container/unknown branches.
function isUtf16beVers(bytes: Buffer, offset: number): boolean {
  const text = 'VERS';
  for (let i = 0; i < text.length; i++) {
    if (bytes[offset + i * 2] !== 0 || bytes[offset + i * 2 + 1] !== text.charCodeAt(i)) return false;
  }
  return true;
}

export function sniffZemaxBytes(bytes: Buffer): ZemaxByteKind {
  if (bytes.length >= 12) {
    if (bytes[0] === 0xff && bytes[1] === 0xfe && bytes.toString('utf16le', 2, 10) === 'VERS') return 'zmx-text'; // UTF-16LE BOM
    if (bytes[0] === 0xfe && bytes[1] === 0xff && isUtf16beVers(bytes, 2)) return 'zmx-text'; // UTF-16BE BOM
    if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf && bytes.toString('latin1', 3, 7) === 'VERS') return 'zmx-text'; // UTF-8 BOM
    if (bytes.toString('utf16le', 0, 8) === 'VERS') return 'zmx-text'; // bare UTF-16LE, no BOM
    if (bytes.toString('latin1', 0, 4) === 'VERS') return 'zmx-text'; // bare ASCII/latin1
    const tag = bytes.readUInt16LE(0);
    if (tag === 0x03ec || bytes[0] === 0xea) return 'zar';
    if (tag === 0x03e8) return 'zprj';
  }
  return 'unknown';
}

function layoutFor(bytes: Buffer): ContainerLayout {
  const tag = bytes.readUInt16LE(0);
  if (tag === 0x03e8) return ZPRJ;
  if (tag === 0x03ec) return ZAR_LATEST;
  if (bytes[0] === 0xea) return ZAR_EARLY;
  throw new Error(`Unrecognized Zemax container header 0x${tag.toString(16)}`);
}

export function readContainerMembers(bytes: Buffer): Array<{ name: string; data: Buffer }> {
  const layout = layoutFor(bytes);
  const members: Array<{ name: string; data: Buffer }> = [];
  let pos = 0;
  while (pos < bytes.length) {
    if (pos + layout.headerLength > bytes.length) {
      throw new Error(`Truncated Zemax container: header at ${pos} runs past EOF (${bytes.length})`);
    }
    const header = bytes.subarray(pos, pos + layout.headerLength);
    const packedSize = layout.sizeBytes === 8
      ? Number(header.readBigUInt64LE(layout.sizeOffset))
      : header.readUInt32LE(layout.sizeOffset);
    const rawName = header.toString(layout.nameEncoding, layout.nameOffset);
    const nul = rawName.indexOf('\0');
    let name = nul === -1 ? rawName : rawName.slice(0, nul);
    if (pos + layout.headerLength + packedSize > bytes.length) {
      throw new Error(`Truncated Zemax container: member "${name}" (${packedSize} bytes) at ${pos} runs past EOF`);
    }
    let data = bytes.subarray(pos + layout.headerLength, pos + layout.headerLength + packedSize);
    if (name.toUpperCase().endsWith('.LZW')) {
      data = lzwDecompress(data);
      name = name.slice(0, -4);
    }
    members.push({ name, data });
    pos += layout.headerLength + packedSize;
  }
  if (pos !== bytes.length) {
    throw new Error(`Misaligned Zemax container walk: ended at ${pos} of ${bytes.length}`);
  }
  return members;
}

const LENS_MEMBER = /\.(zmx|zos)$/i;

export function extractLensZmx(bytes: Buffer): { zmx: Buffer; ignoredMembers: string[] } {
  const members = readContainerMembers(bytes);
  const lens = members.filter((m) => LENS_MEMBER.test(m.name));
  if (lens.length === 0) {
    throw new Error(`Zemax container holds no .zmx lens file (members: ${members.map((m) => m.name).join(', ')})`);
  }
  if (lens.length > 1) {
    throw new Error(`Zemax container holds multiple lens files (${lens.map((m) => m.name).join(', ')}) — re-save the archive with a single lens`);
  }
  return { zmx: lens[0].data, ignoredMembers: members.filter((m) => m !== lens[0]).map((m) => m.name) };
}

/** Every extension `fileToLlm`/the server import route accept as a Zemax system. */
export const ZEMAX_IMPORT_EXTENSIONS = ['.zmx', '.zos', '.zar', '.zprj'] as const;

/**
 * Resolve any Zemax import upload's bytes down to the ZMX text they carry, for
 * callers (the embedded merit/tolerance install) that need the .zmx content
 * regardless of whether the upload was plain text or a real container.
 * Returns null for bytes that don't sniff as a known Zemax kind — the import
 * itself will already have failed on such bytes, so callers observing null
 * here are past a dead branch.
 */
export function zmxTextBytesFromImport(bytes: Buffer): Buffer | null {
  const kind = sniffZemaxBytes(bytes);
  if (kind === 'zmx-text') return bytes;
  if (kind === 'zar' || kind === 'zprj') return extractLensZmx(bytes).zmx;
  return null;
}

// Decompression-bomb guard for untrusted uploads: a crafted member can grow
// quadratically (each KwKwK emission both outputs and dictionaries a
// one-byte-longer word), so cumulative output is capped well above any real
// member — the largest one observed across real archives is under 500 KB.
const MAX_LZW_OUTPUT_BYTES = 128 * 1024 * 1024;

/**
 * Zemax's LZW variant: big-endian bit stream, dictionary seeded with the 256
 * single-byte words, code width starts at 9 bits and grows by one each time the
 * dictionary reaches 2^width. No clear/EOF codes — decoding stops when fewer
 * than `width` bits remain.
 */
export function lzwDecompress(data: Buffer): Buffer {
  const words: Buffer[] = [];
  for (let i = 0; i < 256; i++) words.push(Buffer.from([i]));

  const out: Buffer[] = [];
  let previous: Buffer = Buffer.alloc(0);
  let width = 8;
  let bitPos = 0;
  let outputBytes = 0;
  const totalBits = data.length * 8;

  const readCode = (w: number): number => {
    let code = 0;
    for (let i = 0; i < w; i++, bitPos++) {
      code = (code << 1) | ((data[bitPos >> 3] >> (7 - (bitPos & 7))) & 1);
    }
    return code;
  };

  for (;;) {
    if (words.length >= 1 << width) width++;
    if (bitPos + width > totalBits) break;
    const code = readCode(width);
    if (code > words.length || (code === words.length && previous.length === 0)) {
      throw new Error(`Invalid LZW code ${code} (dictionary has ${words.length} entries)`);
    }
    const word = code < words.length
      ? words[code]
      : Buffer.concat([previous, previous.subarray(0, 1)]);
    outputBytes += word.length;
    if (outputBytes > MAX_LZW_OUTPUT_BYTES) {
      throw new Error(`LZW output exceeds ${MAX_LZW_OUTPUT_BYTES} byte cap`);
    }
    out.push(word);
    if (previous.length > 0) words.push(Buffer.concat([previous, word.subarray(0, 1)]));
    previous = word;
  }
  return Buffer.concat(out);
}
